import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/api/http";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Car, ChevronLeft, Plus, Trash2, Pencil, Wrench } from "lucide-react";

type Cliente = {
  id: string;
  nome: string;
  telefone?: string | null;
  documento?: string | null;
};

type Veiculo = {
  id: string;
  clienteId: string;
  placa?: string | null;
  marca?: string | null;
  modelo?: string | null;
  ano?: number | null;
  km?: number | null;
  createdAt?: string;
};

function onlyDigits(v: string) {
  return (v || "").replace(/\D/g, "");
}

function upperPlate(v: string) {
  return (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
}

export default function ClienteVeiculos() {
  const { id: clienteId } = useParams();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loadingCliente, setLoadingCliente] = useState(true);

  const [loadingList, setLoadingList] = useState(true);
  const [rows, setRows] = useState<Veiculo[]>([]);

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    placa: "",
    marca: "",
    modelo: "",
    ano: "",
    km: "",
  });

  // edição inline
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    placa: "",
    marca: "",
    modelo: "",
    ano: "",
    km: "",
  });

  const isEditing = useMemo(() => !!editingId, [editingId]);

  async function loadCliente() {
    if (!clienteId) return;
    setLoadingCliente(true);
    try {
      const { data } = await api.get(`/api/clientes/${clienteId}`);
      setCliente(data?.cliente ?? null);
    } catch (err: any) {
      toast.error("Não foi possível carregar o cliente");
      navigate("/clientes");
    } finally {
      setLoadingCliente(false);
    }
  }

  async function loadVeiculos() {
    if (!clienteId) return;
    setLoadingList(true);
    try {
      const { data } = await api.get(`/api/clientes/${clienteId}/veiculos`);
      setRows(data?.data ?? data?.veiculos ?? []);
    } catch (err: any) {
      toast.error("Não foi possível carregar os veículos");
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => {
    loadCliente();
    loadVeiculos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  function onChangeNew(name: string, value: string) {
    setForm((p) => ({ ...p, [name]: value }));
  }

  function onChangeEdit(name: string, value: string) {
    setEditForm((p) => ({ ...p, [name]: value }));
  }

  async function createVeiculo() {
    if (!clienteId) return;

    setSaving(true);
    try {
      const payload = {
        placa: form.placa ? upperPlate(form.placa) : null,
        marca: form.marca?.trim() || null,
        modelo: form.modelo?.trim() || null,
        ano: form.ano ? Number(onlyDigits(form.ano)) : null,
        km: form.km ? Number(onlyDigits(form.km)) : null,
      };

      await api.post(`/api/clientes/${clienteId}/veiculos`, payload);
      toast.success("Veículo cadastrado");

      setForm({ placa: "", marca: "", modelo: "", ano: "", km: "" });
      await loadVeiculos();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao cadastrar veículo");
    } finally {
      setSaving(false);
    }
  }

  function startEdit(v: Veiculo) {
    setEditingId(v.id);
    setEditForm({
      placa: v.placa ?? "",
      marca: v.marca ?? "",
      modelo: v.modelo ?? "",
      ano: v.ano ? String(v.ano) : "",
      km: v.km ? String(v.km) : "",
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({ placa: "", marca: "", modelo: "", ano: "", km: "" });
  }

  async function saveEdit() {
    if (!editingId) return;

    try {
      const payload = {
        placa: editForm.placa ? upperPlate(editForm.placa) : null,
        marca: editForm.marca?.trim() || null,
        modelo: editForm.modelo?.trim() || null,
        ano: editForm.ano ? Number(onlyDigits(editForm.ano)) : null,
        km: editForm.km ? Number(onlyDigits(editForm.km)) : null,
      };

      await api.put(`/api/veiculos/${editingId}`, payload);
      toast.success("Veículo atualizado");
      cancelEdit();
      await loadVeiculos();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao atualizar veículo");
    }
  }

  async function removeVeiculo(veiculoId: string) {
    const ok = confirm("Remover este veículo?");
    if (!ok) return;

    try {
      await api.delete(`/api/veiculos/${veiculoId}`);
      toast.success("Veículo removido");
      await loadVeiculos();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Erro ao remover veículo");
    }
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/clientes")} className="gap-2">
              <ChevronLeft size={16} />
              Voltar
            </Button>

            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Car size={22} />
                Veículos do Cliente
              </h1>

              {loadingCliente ? (
                <p className="text-sm text-slate-500">Carregando cliente...</p>
              ) : (
                <p className="text-sm text-slate-500">
                  <span className="font-medium text-slate-800">{cliente?.nome}</span>
                  {cliente?.telefone ? ` • ${cliente.telefone}` : ""}
                  {cliente?.documento ? ` • ${cliente.documento}` : ""}
                </p>
              )}
            </div>
          </div>

          <Link to={`/clientes/${clienteId}/editar`}>
            <Button variant="outline" className="gap-2">
              <Pencil size={16} />
              Editar Cliente
            </Button>
          </Link>
        </div>

        {/* Cadastro rápido */}
        <div className="bg-white p-6 rounded-xl shadow-sm space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-semibold text-slate-800">Cadastrar veículo</h2>
            <Button onClick={createVeiculo} disabled={saving || !clienteId} className="gap-2">
              <Plus size={16} />
              {saving ? "Salvando..." : "Adicionar"}
            </Button>
          </div>

          <div className="grid grid-cols-5 gap-3">
            <Input
              name="placa"
              placeholder="Placa (ABC1D23)"
              value={form.placa}
              onChange={(e) => onChangeNew("placa", upperPlate(e.target.value))}
            />
            <Input
              name="marca"
              placeholder="Marca"
              value={form.marca}
              onChange={(e) => onChangeNew("marca", e.target.value)}
            />
            <Input
              name="modelo"
              placeholder="Modelo"
              value={form.modelo}
              onChange={(e) => onChangeNew("modelo", e.target.value)}
            />
            <Input
              name="ano"
              placeholder="Ano"
              value={form.ano}
              onChange={(e) => onChangeNew("ano", onlyDigits(e.target.value).slice(0, 4))}
            />
            <Input
              name="km"
              placeholder="KM"
              value={form.km}
              onChange={(e) => onChangeNew("km", onlyDigits(e.target.value).slice(0, 8))}
            />
          </div>

          <p className="text-xs text-slate-500">
            Dica: placa é opcional. Se não tiver, dá pra cadastrar mesmo assim e completar depois.
          </p>
        </div>

        {/* Lista */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-slate-50">
            <p className="text-sm text-slate-700">
              {loadingList ? "Carregando veículos..." : `${rows.length} veículo(s)`}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white text-slate-600">
                <tr className="border-b">
                  <th className="text-left px-4 py-3">Placa</th>
                  <th className="text-left px-4 py-3">Veículo</th>
                  <th className="text-left px-4 py-3">Ano</th>
                  <th className="text-left px-4 py-3">KM</th>
                  <th className="text-right px-4 py-3">Ações</th>
                </tr>
              </thead>

              <tbody>
                {loadingList && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                      Carregando...
                    </td>
                  </tr>
                )}

                {!loadingList && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                      Nenhum veículo cadastrado para este cliente.
                    </td>
                  </tr>
                )}

                {!loadingList &&
                  rows.map((v) => {
                    const editing = editingId === v.id;

                    return (
                      <tr key={v.id} className="border-t hover:bg-slate-50">
                        {/* Placa */}
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {editing ? (
                            <Input
                              value={editForm.placa}
                              onChange={(e) => onChangeEdit("placa", upperPlate(e.target.value))}
                            />
                          ) : (
                            v.placa || "-"
                          )}
                        </td>

                        {/* Veículo */}
                        <td className="px-4 py-3">
                          {editing ? (
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                value={editForm.marca}
                                onChange={(e) => onChangeEdit("marca", e.target.value)}
                                placeholder="Marca"
                              />
                              <Input
                                value={editForm.modelo}
                                onChange={(e) => onChangeEdit("modelo", e.target.value)}
                                placeholder="Modelo"
                              />
                            </div>
                          ) : (
                            <span className="text-slate-800">
                              {[v.marca, v.modelo].filter(Boolean).join(" ") || "-"}
                            </span>
                          )}
                        </td>

                        {/* Ano */}
                        <td className="px-4 py-3">
                          {editing ? (
                            <Input
                              value={editForm.ano}
                              onChange={(e) => onChangeEdit("ano", onlyDigits(e.target.value).slice(0, 4))}
                              placeholder="Ano"
                            />
                          ) : (
                            v.ano ?? "-"
                          )}
                        </td>

                        {/* KM */}
                        <td className="px-4 py-3">
                          {editing ? (
                            <Input
                              value={editForm.km}
                              onChange={(e) => onChangeEdit("km", onlyDigits(e.target.value).slice(0, 8))}
                              placeholder="KM"
                            />
                          ) : (
                            v.km ?? "-"
                          )}
                        </td>

                        {/* Ações */}
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2 flex-wrap">
                            {/* Atalho OS (vai existir depois) */}
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-2"
                              onClick={() => {
                                toast.message("Atalho pronto ✅", {
                                  description: "Quando criarmos a OS, este botão vai abrir a criação já com cliente e veículo.",
                                });
                                // futuro:
                                // navigate(`/ordens/nova?clienteId=${clienteId}&veiculoId=${v.id}`);
                              }}
                            >
                              <Wrench size={16} />
                              Criar OS
                            </Button>

                            {!editing ? (
                              <>
                                <Button size="sm" variant="outline" className="gap-2" onClick={() => startEdit(v)}>
                                  <Pencil size={16} />
                                  Editar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-2"
                                  onClick={() => removeVeiculo(v.id)}
                                >
                                  <Trash2 size={16} />
                                  Remover
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" className="gap-2" onClick={saveEdit}>
                                  Salvar
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEdit}>
                                  Cancelar
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
